import React, { ReactElement } from 'react';
import { useIntl } from 'react-intl';
import StaticPageComponent from '@fuww/library/src/StaticPage';
import { NavTabs } from '@fuww/library/src/Tabs';
import { useRouter } from 'next/router';
import Head from '../components/Head';
import Layout from '../components/Layout';
import messages from '../lib/messages.mjs';
import generateIntelligencePageBreadcrumbs
  from '../lib/generateIntelligencePageBreadcrumbs';
import Hero from '../components/Hero';
import redirectRouteToDomain from '../lib/redirectRouteToDomain';
import Tab from '../components/Tab';
import Markdown from '../components/Markdown';
import globalMarkdown
  from '../data/staticPages/global-fashion-industry-statistics/global.md';
// eslint-disable-next-line max-len
import europeanUnionMarkdown from '../data/staticPages/global-fashion-industry-statistics/european-union.md';
import franceMarkdown
  from '../data/staticPages/global-fashion-industry-statistics/france.md';
import germanyMarkdown
  from '../data/staticPages/global-fashion-industry-statistics/germany.md';
import spainMarkdown
  from '../data/staticPages/global-fashion-industry-statistics/spain.md';
import italyMarkdown
  from '../data/staticPages/global-fashion-industry-statistics/italy.md';
import usaMarkdown
  from '../data/staticPages/global-fashion-industry-statistics/usa.md';

const regionOptions = [
  {
    route: 'global-fashion-industry-statistics',
    label: 'Global',
    value: 'global',
  },
  {
    route: 'global-fashion-industry-statistics',
    params: { region: 'european-union' },
    label: 'EU',
    value: 'european-union',
  },
  {
    route: 'global-fashion-industry-statistics',
    params: { region: 'france' },
    label: 'France',
    value: 'france',
  },
  {
    route: 'global-fashion-industry-statistics',
    params: { region: 'germany' },
    label: 'Germany',
    value: 'germany',
  },
  {
    route: 'global-fashion-industry-statistics',
    params: { region: 'spain' },
    label: 'Spain',
    value: 'spain',
  },
  {
    route: 'global-fashion-industry-statistics',
    params: { region: 'italy' },
    label: 'Italy',
    value: 'italy',
  },
  {
    route: 'global-fashion-industry-statistics',
    params: { region: 'usa' },
    label: 'USA',
    value: 'usa',
  },
];

const GlobalFashionIndustryStatisticsPage = () => {
  const intl = useIntl();
  const { query } = useRouter();
  const region = String(query.region ?? 'global');

  const breadcrumbs = generateIntelligencePageBreadcrumbs(
    intl,
    'global-fashion-industry-statistics',
    'globalFashionIndustryStatistics',
  );

  const getMarkdownContent = () => {
    switch (region) {
      case 'european-union': {
        return europeanUnionMarkdown;
      }
      case 'france': {
        return franceMarkdown;
      }
      case 'germany': {
        return germanyMarkdown;
      }
      case 'spain': {
        return spainMarkdown;
      }
      case 'italy': {
        return italyMarkdown;
      }
      case 'usa': {
        return usaMarkdown;
      }
      default: {
        return globalMarkdown;
      }
    }
  };

  return (
    <>
      <Head
        title={intl.formatMessage(
          messages['globalFashionIndustryStatistics.title'],
        )}
        description={intl.formatMessage(
          messages['globalFashionIndustryStatistics.description'],
        )}
      />
      <StaticPageComponent
        breadcrumbs={breadcrumbs}
        content={(
          <>
            <NavTabs
              NavTab={Tab}
              value={region}
              tabs={regionOptions}
            />
            <Markdown markdown={getMarkdownContent()} allowHtml allowGfm />
          </>
        )}
        title={intl.formatMessage(
          messages['globalFashionIndustryStatistics.title'],
        )}
        subTitle={intl.formatMessage(
          messages['globalFashionIndustryStatistics.subTitle'],
        )}
        withTableStyles
      />
    </>
  );
};

GlobalFashionIndustryStatisticsPage.getLayout = (page: ReactElement) => (
  <Layout header={(
    <Hero
      alt="Global Fashion Industry Statistics"
    />
  )}
  >
    {page}
  </Layout>
);

GlobalFashionIndustryStatisticsPage.getInitialProps = redirectRouteToDomain(
  'global-fashion-industry-statistics',
);

export default GlobalFashionIndustryStatisticsPage;
